import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { User, AuthContextType } from '../types';
import { authService } from '../services/authService';
import { enqueueSnackbar } from 'notistack';
import { DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';

/**
 * Decoded JWT payload containing permission scopes and subscription features
 */
export interface DecodedJWT {
  userId: string;
  schoolId?: string;
  roleId?: string;
  isSystemAdmin: boolean;
  roleName?: string;
  isSchoolAdmin?: boolean;
  // True only for holders of the system-generated Teacher role. Drives the
  // class-teacher / subject-teacher visibility + write restrictions across
  // the app. Any other role with the same module scopes is unrestricted.
  isTeacher?: boolean;
  scopes: string[];
  features: string[];
  subscriptionStatus?: 'active' | 'trial' | 'expired' | 'cancelled' | 'suspended' | null;
  subscriptionEndDate?: string;
  permissionVersion?: number;
  iat?: number;
  exp?: number;
}

/**
 * Decode JWT token payload (no verification - backend already verified)
 */
const decodeJWT = (token: string): DecodedJWT | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded as DecodedJWT;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Extended Auth Context Type with JWT data and timezone
 */
interface ExtendedAuthContextType extends AuthContextType {
  decodedToken: DecodedJWT | null;
  /** School's timezone from settings, defaults to Asia/Karachi (PKT) */
  timezone: string;
  /** School's currency code from settings, defaults to PKR */
  currency: string;
  /** School's fee billing day (1-28) — the day each installment lands on. Default 1. */
  feeBillingDay: number;
}

const AuthContext = createContext<ExtendedAuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<DecodedJWT | null>(null);
  const [loading, setLoading] = useState(true);

  // Decode token whenever it changes
  useEffect(() => {
    if (token) {
      const decoded = decodeJWT(token);
      setDecodedToken(decoded);
    } else {
      setDecodedToken(null);
    }
  }, [token]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          // Check if token is expired
          const decoded = decodeJWT(storedToken);
          if (decoded?.exp && decoded.exp * 1000 < Date.now()) {
            // Token expired, clear auth state
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
            setLoading(false);
            return;
          }

          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          
          // Verify token is still valid by fetching profile
          try {
            const userProfile = await authService.getProfile();
            setUser(userProfile || null);
          } catch (error) {
            // Token is invalid, clear auth state
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (identifier: string, password: string) => {
    try {
      const { token: authToken, user: userData } = await authService.login({ identifier, password });
      
      // Store in localStorage
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Update state
      setToken(authToken);
      setUser(userData);
      
      enqueueSnackbar('Login successful!', { variant: 'success' });
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear localStorage and state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      setDecodedToken(null);
      enqueueSnackbar('Logged out successfully', { variant: 'success' });
    }
  };

  const updateProfile = async (data: { name: string; email?: string }) => {
    try {
      const updatedUser = await authService.updateProfile(data);
      
      // Update localStorage and state
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser || null);
      
      enqueueSnackbar('Profile updated successfully!', { variant: 'success' });
    } catch (error: any) {
      const errorMessage = error.message || 'Profile update failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authService.changePassword({ currentPassword, newPassword });
      enqueueSnackbar('Password changed successfully!', { variant: 'success' });
    } catch (error: any) {
      const errorMessage = error.message || 'Password change failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    }
  };

  const updateUser = (updatedUser: User) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  // Get timezone from school settings, fallback to default (PKT)
  const timezone = useMemo(() => {
    if (user?.schoolId?.settings?.timezone) {
      return user.schoolId.settings.timezone;
    }
    return DEFAULT_TIMEZONE;
  }, [user?.schoolId?.settings?.timezone]);

  // Currency code from school settings — used by every money-formatting call
  // across the app. Falls back to PKR to keep the demo tenants sane.
  const currency = useMemo(() => {
    const raw = user?.schoolId?.settings?.currency;
    return raw && raw.trim().length > 0 ? raw.trim().toUpperCase() : 'PKR';
  }, [user?.schoolId?.settings?.currency]);

  // Fee billing day — day of month every installment snaps to. Default 1.
  const feeBillingDay = useMemo(() => {
    const raw = (user?.schoolId?.settings as any)?.feeBillingDay;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 1 && n <= 28 ? n : 1;
  }, [(user?.schoolId?.settings as any)?.feeBillingDay]);

  const value: ExtendedAuthContextType = {
    user,
    token,
    loading,
    login,
    logout,
    updateProfile,
    updateUser,
    changePassword,
    decodedToken,
    timezone,
    currency,
    feeBillingDay,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 